import React, { useState, useEffect } from 'react';
import { Search, Eye, Filter, Building2, MapPin, Phone, Mail, Globe, Calendar, Users, X, Video, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { admissionsService } from '../../firebase/admissions';
import toast from 'react-hot-toast';
import R2Image from '../../components/R2Image';

const AllInstitutions = () => {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // all, name, id, type, city
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingVirtualTour, setEditingVirtualTour] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadInstitutions();
  }, []);

  useEffect(() => {
    filterInstitutions();
  }, [searchTerm, searchType, institutions]);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAllInstitutions();
      if (result.success) {
        setInstitutions(result.institutions || []);
      } else {
        toast.error('Failed to load institutions: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error loading institutions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterInstitutions = () => {
    if (!searchTerm.trim()) {
      setFilteredInstitutions(institutions);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    let filtered = [...institutions];

    switch (searchType) {
      case 'name':
        filtered = filtered.filter(inst => 
          inst.name?.toLowerCase().includes(term)
        );
        break;
      case 'id':
        filtered = filtered.filter(inst => 
          inst.id?.toLowerCase().includes(term) ||
          inst.coachingId?.toLowerCase().includes(term)
        );
        break;
      case 'type':
        filtered = filtered.filter(inst => 
          inst.type?.toLowerCase().includes(term)
        );
        break;
      case 'city':
        filtered = filtered.filter(inst => 
          inst.address?.city?.toLowerCase().includes(term)
        );
        break;
      default:
        // Search all fields
        filtered = filtered.filter(inst => 
          inst.name?.toLowerCase().includes(term) ||
          inst.id?.toLowerCase().includes(term) ||
          inst.coachingId?.toLowerCase().includes(term) ||
          inst.type?.toLowerCase().includes(term) ||
          inst.address?.city?.toLowerCase().includes(term) ||
          inst.contact?.email?.toLowerCase().includes(term) ||
          inst.contact?.phone?.toLowerCase().includes(term)
        );
    }

    setFilteredInstitutions(filtered);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school':
        return 'bg-blue-100 text-blue-800';
      case 'college':
        return 'bg-purple-100 text-purple-800';
      case 'coaching':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    // YouTube URL patterns
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo URL patterns
    const vimeoRegex = /(?:vimeo\.com\/)(?:.*\/)?(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // If already an embed URL, return as is
    if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com')) {
      return url;
    }
    
    return url;
  };

  const handleViewDetails = async (institution) => {
    try {
      // Load full institution data including customization
      const [instResult, customizationResult] = await Promise.all([
        admissionsService.getInstitutionById(institution.id),
        admissionsService.getInstitutionCustomization(institution.id)
      ]);

      if (instResult.success) {
        setSelectedInstitution({
          ...instResult.institution,
          customization: customizationResult.success ? customizationResult.customization : null
        });
        setEditingVirtualTour(instResult.institution.virtualTourVideo || '');
        setShowDetails(true);
        setShowEditForm(false);
      } else {
        toast.error('Failed to load institution details');
      }
    } catch (error) {
      toast.error('Error loading institution details');
    }
  };

  const handleSaveVirtualTour = async () => {
    if (!selectedInstitution) return;

    try {
      setSaving(true);
      const updatedData = {
        ...selectedInstitution,
        virtualTourVideo: editingVirtualTour.trim()
      };
      
      const result = await admissionsService.saveInstitution(updatedData, selectedInstitution.id);
      
      if (result.success) {
        toast.success('Virtual tour video updated successfully');
        setSelectedInstitution(updatedData);
        setShowEditForm(false);
        // Reload institutions list
        loadInstitutions();
      } else {
        toast.error('Failed to update virtual tour video: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error updating virtual tour video: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">All Institutions</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View and manage all schools, colleges, and coaching centers
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search institutions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Fields</option>
              <option value="name">Institution Name</option>
              <option value="id">Institution ID</option>
              <option value="type">Type</option>
              <option value="city">City</option>
            </select>
          </div>
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Found {filteredInstitutions.length} institution(s)
          </p>
        )}
      </div>

      {/* Institutions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInstitutions.map((institution) => (
          <div
            key={institution.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
            onClick={() => handleViewDetails(institution)}
          >
            {/* Image */}
            <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 relative">
              {institution.profilePicture ? (
                <R2Image
                  fileKey={institution.profilePicture}
                  alt={institution.name}
                  className="w-full h-full object-cover"
                />
              ) : institution.logo ? (
                <R2Image
                  fileKey={institution.logo}
                  alt={institution.name}
                  className="w-full h-full object-cover"
                />
              ) : null}
              <div className={`absolute top-4 right-4 px-3 py-1 rounded-full ${getTypeColor(institution.type)} flex items-center gap-2`}>
                <Building2 className="w-4 h-4" />
                <span className="capitalize text-sm font-medium">{institution.type}</span>
              </div>
              {!institution.isActive && (
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                  Inactive
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{institution.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                {institution.description || 'No description available'}
              </p>

              {/* Location */}
              {institution.address?.city && (
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-2">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{institution.address.city}</span>
                  {institution.address.state && <span>, {institution.address.state}</span>}
                </div>
              )}

              {/* Contact */}
              {institution.contact?.phone && (
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-2">
                  <Phone className="w-4 h-4 mr-1" />
                  <span>{institution.contact.phone}</span>
                </div>
              )}

              {/* ID */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Institution ID:</span>
                  <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{institution.id.substring(0, 12)}...</span>
                </div>
                {institution.coachingId && (
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Coaching ID:</span>
                    <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{institution.coachingId.substring(0, 12)}...</span>
                  </div>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails(institution);
                }}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredInstitutions.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm ? 'No institutions found matching your search' : 'No institutions yet'}
          </p>
        </div>
      )}

      {/* Institution Details Modal */}
      {showDetails && selectedInstitution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Institution Details</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedInstitution(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{selectedInstitution.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Type:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium capitalize">{selectedInstitution.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Institution ID:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">{selectedInstitution.id}</span>
                    </div>
                    {selectedInstitution.coachingId && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Coaching ID:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">{selectedInstitution.coachingId}</span>
                      </div>
                    )}
                    {selectedInstitution.established && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Established:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedInstitution.established}</span>
                      </div>
                    )}
                    {selectedInstitution.students && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Students:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{selectedInstitution.students}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${selectedInstitution.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {selectedInstitution.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Application Fee:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">₹{selectedInstitution.applicationFee || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedInstitution.description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedInstitution.description}</p>
                  </div>
                )}

                {/* Address */}
                {selectedInstitution.address && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Address</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedInstitution.address.street && <p>{selectedInstitution.address.street}</p>}
                      <p>
                        {selectedInstitution.address.city}
                        {selectedInstitution.address.state && `, ${selectedInstitution.address.state}`}
                        {selectedInstitution.address.pincode && ` - ${selectedInstitution.address.pincode}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Contact */}
                {selectedInstitution.contact && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Contact Information</h3>
                    <div className="space-y-1 text-sm">
                      {selectedInstitution.contact.phone && (
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">{selectedInstitution.contact.phone}</span>
                        </p>
                      )}
                      {selectedInstitution.contact.email && (
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Email:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">{selectedInstitution.contact.email}</span>
                        </p>
                      )}
                      {selectedInstitution.contact.website && (
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Website:</span>
                          <a href={selectedInstitution.contact.website} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                            {selectedInstitution.contact.website}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Profile Picture */}
                {selectedInstitution.profilePicture && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Profile Picture</h3>
                    <R2Image
                      fileKey={selectedInstitution.profilePicture}
                      alt={selectedInstitution.name}
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                )}

                {/* Virtual Tour Video */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Video className="w-5 h-5" />
                      Virtual Tour
                    </h3>
                    {!showEditForm && (
                      <button
                        onClick={() => setShowEditForm(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        {selectedInstitution.virtualTourVideo ? 'Edit' : 'Add Video'}
                      </button>
                    )}
                  </div>
                  
                  {showEditForm ? (
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={editingVirtualTour}
                        onChange={(e) => setEditingVirtualTour(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Simply paste any YouTube video URL (watch or youtu.be format)
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveVirtualTour}
                          disabled={saving}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setShowEditForm(false);
                            setEditingVirtualTour(selectedInstitution.virtualTourVideo || '');
                          }}
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : selectedInstitution.virtualTourVideo ? (
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={getEmbedUrl(selectedInstitution.virtualTourVideo)}
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Virtual Tour"
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No virtual tour video added yet
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => navigate(`/admissions/${selectedInstitution.id}`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    View Public Page
                  </button>
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedInstitution(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllInstitutions;

